import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';
import _ from 'lodash';
import diff from 'object-diff';
import * as Yup from 'yup';

import { booleanOptionsForRadioGroup } from '../../../../../features/forms/helpers/random-form-helpers';

import {
  requestDocuments,
  selectCargoDocumentsByTitle,
} from '../../../slices/documentsSlice';
import {
  getDangerousGoodsData,
  getInspections,
  selectCargo,
  selectDangerousGoods,
  selectGeneralData,
  selectInspections,
  updateCargo,
  updateDangerousGoodsData,
  updateInspections,
  updatePortCall,
} from '../../../slices/portCallSlice';

import DangerousGoodsOptionsCheckboxGroup from '../../../../forms/components/DangerousGoodsOptionsCheckboxGroup';
import Input from '../../../../forms/components/Input';
import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import Textarea from '../../../../forms/components/TextArea';
import { FormRefContext } from '../../../../global/context/FormRefContext';
import PageHeader from '../../../../layout/components/PageHeader';
import {
  CARGO_DOUMENTS,
  DocumentCategory,
  DocumentSubCategory,
} from '../../documents/constants';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';

import '../styles/cargo-information.scss';

const validationSchema = Yup.object({
  isVesselCarryingCargo: Yup.boolean().required('This field is required'),
  isTransportingHeavyOils: Yup.boolean().when('isVesselCarryingCargo', {
    is: true,
    then: (schema) => schema.required('This field is required'),
  }),
  dangerousGoodsType: Yup.array().when('isVesselCarryingCargo', {
    is: true,
    then: (schema) => schema.required('This field is required'),
  }),
  arrivalCargoDescription: Yup.string().when('isVesselCarryingCargo', {
    is: true,
    then: (schema) => schema.required('This field is required'),
  }),
  arrivalCargoVolume: Yup.number()
    .nullable()
    .min(0, 'Must be a positive number'),
  departureCargoVolume: Yup.number()
    .nullable()
    .min(0, 'Must be a positive number'),
});

const IS_UNLOADING_DANGEROUS_GOODS = 'isUnloadingDangerousGoods';
const IS_LOADING_DANGEROUS_GOODS = 'isLoadingDangerousGoods';
const IS_DANGEROUS_GOODS_IN_TRANSIT = 'isDangerousGoodsInTransit';
const IS_NOT_CARRYING_DANGEROUS_GOODS = 'isNotCarryingDangerousGoods';

const allDangerousGoodsStatusValues = [
  IS_NOT_CARRYING_DANGEROUS_GOODS,
  IS_UNLOADING_DANGEROUS_GOODS,
  IS_LOADING_DANGEROUS_GOODS,
  IS_DANGEROUS_GOODS_IN_TRANSIT,
];

const dangerousGoodsTypeOptions = [
  { key: 'Unloading dangerous goods ', value: IS_UNLOADING_DANGEROUS_GOODS },
  { key: 'Loading dangerous goods ', value: IS_LOADING_DANGEROUS_GOODS },
  { key: 'Dangerous goods in transit ', value: IS_DANGEROUS_GOODS_IN_TRANSIT },
  {
    key: 'Not carrying dangerous goods ',
    value: IS_NOT_CARRYING_DANGEROUS_GOODS,
  },
];

const cargoTanksConditionOptions = [
  { key: 'Full', value: 'Full' },
  { key: 'Empty', value: 'Empty' },
  { key: 'Inerted', value: 'Inerted' },
];
const ballastTankConditionOptions = [
  { key: 'Full', value: 'Full' },
  { key: 'Empty', value: 'Empty' },
  { key: 'Inerted', value: 'Inerted' },
];

const vesselCarryingCargoOptions = booleanOptionsForRadioGroup();

function CargoInformation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    dispatch(getDangerousGoodsData(params.uid));
    dispatch(getInspections(params.uid));
  }, [dispatch, params.uid]);

  const { arrivalCargo, departureCargo, declaredCargo } =
    useSelector(selectCargo);
  const cargoDocumentsByTitle = useSelector(selectCargoDocumentsByTitle);
  const dangerousGoods = useSelector(selectDangerousGoods);
  const { isVesselCarryingCargo } = useSelector(selectGeneralData);
  const inspections = useSelector(selectInspections);

  const formRef = useRef();
  const { formContextRef } = useContext(FormRefContext);

  function getCargoDocumentsToRequest() {
    let cargoDocs,
      manifestDoc,
      billOfLadingDoc,
      qualityCertificateDoc,
      loadingDischargingSequenceDoc;
    if (!_.isEmpty(cargoDocumentsByTitle)) {
      cargoDocs = cargoDocumentsByTitle[DocumentSubCategory.CargoDocuments];
      manifestDoc = cargoDocs?.find(
        (doc) => doc.documentType.id === CARGO_DOUMENTS.CARGO_MANIFEST.id
      );
      billOfLadingDoc = cargoDocs?.find(
        (doc) => doc.documentType.id === CARGO_DOUMENTS.BILL_OF_LADING.id
      );
      qualityCertificateDoc = cargoDocs?.find(
        (doc) =>
          doc.documentType.id === CARGO_DOUMENTS.QUALITY_CERTIFICATE_OF_CARGO.id
      );
      loadingDischargingSequenceDoc = cargoDocs?.find(
        (doc) =>
          doc.documentType.id === CARGO_DOUMENTS.LOADING_DISCHARGING_SEQUENCE.id
      );
    }
    let documentsToRequest = [];
    if (!manifestDoc)
      documentsToRequest.push({
        documentType: {
          id: CARGO_DOUMENTS.CARGO_MANIFEST.id,
          name: CARGO_DOUMENTS.CARGO_MANIFEST.name,
          category: DocumentCategory.CARGO_DOCUMENTS,
          subcategory: DocumentSubCategory.CargoDocuments,
        },
        name: CARGO_DOUMENTS.CARGO_MANIFEST.name,
        status: 'Draft',
      });

    if (!billOfLadingDoc)
      documentsToRequest.push({
        documentType: {
          id: CARGO_DOUMENTS.BILL_OF_LADING.id,
          name: CARGO_DOUMENTS.BILL_OF_LADING.name,
          category: DocumentCategory.CARGO_DOCUMENTS,
          subcategory: DocumentSubCategory.CargoDocuments,
        },
        name: CARGO_DOUMENTS.BILL_OF_LADING.name,
        status: 'Draft',
      });

    if (!qualityCertificateDoc)
      documentsToRequest.push({
        documentType: {
          id: CARGO_DOUMENTS.QUALITY_CERTIFICATE_OF_CARGO.id,
          name: CARGO_DOUMENTS.QUALITY_CERTIFICATE_OF_CARGO.name,
          category: DocumentCategory.CARGO_DOCUMENTS,
          subcategory: DocumentSubCategory.CargoDocuments,
        },
        name: CARGO_DOUMENTS.QUALITY_CERTIFICATE_OF_CARGO.name,
        status: 'Draft',
      });

    if (!loadingDischargingSequenceDoc)
      documentsToRequest.push({
        documentType: {
          id: CARGO_DOUMENTS.LOADING_DISCHARGING_SEQUENCE.id,
          name: CARGO_DOUMENTS.LOADING_DISCHARGING_SEQUENCE.name,
          category: DocumentCategory.CARGO_DOCUMENTS,
          subcategory: DocumentSubCategory.CargoDocuments,
        },
        name: CARGO_DOUMENTS.LOADING_DISCHARGING_SEQUENCE.name,
        status: 'Draft',
      });

    if (documentsToRequest && cargoDocs) {
      documentsToRequest = documentsToRequest.concat(cargoDocs);
    }
    return documentsToRequest;
  }

  useEffect(() => {
    formContextRef.current = formRef.current;
  }, [
    formContextRef,
    formRef,
    arrivalCargo,
    departureCargo,
    isVesselCarryingCargo,
    inspections,
  ]);

  const computeInitialValues = () => {
    let initialValues = {
      arrivalCargoDescription: arrivalCargo?.description ?? undefined,
      departureCargoDescription: departureCargo?.description ?? undefined,
      arrivalCargoVolume: arrivalCargo?.volume ?? undefined,
      departureCargoVolume: departureCargo?.volume ?? undefined,
      dangerousGoodsType: [IS_NOT_CARRYING_DANGEROUS_GOODS],
      isVesselCarryingCargo,
      isTransportingHeavyOils: undefined,
    };

    const { dangerousGoodsData } = dangerousGoods;

    if (dangerousGoodsData) {
      initialValues = {
        ...initialValues,
        ...JSON.parse(JSON.stringify(dangerousGoodsData)),
      };

      if (dangerousGoodsData.dangerousGoodsStatus) {
        initialValues.dangerousGoodsType = [];

        if (
          dangerousGoodsData.dangerousGoodsStatus[IS_DANGEROUS_GOODS_IN_TRANSIT]
        ) {
          initialValues.dangerousGoodsType.push(IS_DANGEROUS_GOODS_IN_TRANSIT);
        }

        if (
          dangerousGoodsData.dangerousGoodsStatus[IS_UNLOADING_DANGEROUS_GOODS]
        ) {
          initialValues.dangerousGoodsType.push(IS_UNLOADING_DANGEROUS_GOODS);
        }

        if (
          dangerousGoodsData.dangerousGoodsStatus[IS_LOADING_DANGEROUS_GOODS]
        ) {
          initialValues.dangerousGoodsType.push(IS_LOADING_DANGEROUS_GOODS);
        }

        if (
          dangerousGoodsData.dangerousGoodsStatus[
            IS_NOT_CARRYING_DANGEROUS_GOODS
          ]
        ) {
          initialValues.dangerousGoodsType = [IS_NOT_CARRYING_DANGEROUS_GOODS];
        }
      }
    }

    if (inspections?.data) {
      initialValues.conditionOfCargoTanks =
        inspections?.data?.conditionOfCargoTanks;
      initialValues.conditionOfBallastTanks =
        inspections?.data?.conditionOfBallastTanks;
    }

    return initialValues;
  };

  const initialValues = computeInitialValues();

  const handleSubmit = async (values) => {
    const changedValues = diff(initialValues, values);

    // Check if isVesselCarryingCargo has changed and dispatch updatePortCall
    if (values.isVesselCarryingCargo !== initialValues.isVesselCarryingCargo) {
      const isSuccess = await dispatch(
        updatePortCall({
          uid: params.uid,
          isVesselCarryingCargo: values.isVesselCarryingCargo,
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccess) return;
    }

    if (
      values.isVesselCarryingCargo ||
      values.isVesselCarryingCargo === 'true'
    ) {
      let cargoDocsToRequest = getCargoDocumentsToRequest();
      if (cargoDocsToRequest.length > 0) {
        const isSuccess = await dispatch(
          requestDocuments({
            uid: params.uid,
            category: 'cargoDocuments',
            data: cargoDocsToRequest,
          })
        ).then((response) => {
          if (response.type && response.type.endsWith('/rejected'))
            return false;
          else return true;
        });
        if (!isSuccess) return;
      }
    }

    if (
      values.conditionOfCargoTanks !== initialValues.conditionOfCargoTanks ||
      values.conditionOfBallastTanks !== initialValues.conditionOfBallastTanks
    ) {
      let finalValues = { ...inspections?.data };
      finalValues.conditionOfCargoTanks = values.conditionOfCargoTanks;
      finalValues.conditionOfBallastTanks = values.conditionOfBallastTanks;
      const isSuccess = await dispatch(
        updateInspections({
          uid: params.uid,
          data: finalValues,
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccess) return;
    }
    if (Object.keys(changedValues).length) {
      // Create the object structure as needed for updateCargo
      const cargoUpdatePayload = {
        arrivalCargo: {
          ...arrivalCargo,
          description: values.arrivalCargoDescription,
          volume: values.arrivalCargoVolume,
        },
        departureCargo: {
          ...departureCargo,
          description: values.departureCargoDescription,
          volume: values.departureCargoVolume,
        },
        cargoToDeclare: declaredCargo.length > 0,
        ...(declaredCargo.length > 0 && { declaredCargo }),
      };
      const isSuccessUpdateCargo = await dispatch(
        updateCargo(cargoUpdatePayload)
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdateCargo) return;

      const dangerousGoodsStatus = {};
      values.dangerousGoodsType.forEach((key) => {
        dangerousGoodsStatus[key] = true;
      });

      allDangerousGoodsStatusValues.forEach((key) => {
        if (!(key in dangerousGoodsStatus) || !dangerousGoodsStatus[key]) {
          dangerousGoodsStatus[key] = false;
        }
      });

      // Merge the current dangerousGoodsData with the updated dangerousGoodsStatus
      const updatedDangerousGoodsData = {
        ...dangerousGoods.dangerousGoodsData,
        dangerousGoodsStatus,
        isTransportingHeavyOils: values?.isTransportingHeavyOils,
      };
      const isSuccessUpdateDangGoods = await dispatch(
        updateDangerousGoodsData({
          uid: params.uid,
          dangerousGoodsData: updatedDangerousGoodsData,
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdateDangGoods) return;
    }

    navigate(`/${params.uid}/cargo/documents`);
  };

  return (
    <div className="cargo-information">
      <PageHeader title={'Cargo information'} style />
      <Formik
        onSubmit={handleSubmit}
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}>
        {({ values }) => (
          <Form className="cargo-information-form">
            <div className="cargo-information-form__row">
              <RadioButtonsCustom
                name="isVesselCarryingCargo"
                label="Are you carrying or will carry cargo? *"
                options={vesselCarryingCargoOptions}
              />
            </div>
            <div className="cargo-information-form__row">
              <RadioButtonsCustom
                name="conditionOfCargoTanks"
                label="Condition of cargo tanks"
                options={cargoTanksConditionOptions}
              />
              <RadioButtonsCustom
                name="conditionOfBallastTanks"
                label="Condition of ballast tanks"
                options={ballastTankConditionOptions}
              />
            </div>

            {values.isVesselCarryingCargo ? (
              <div className="cargo-information-form__controls">
                <div className="cargo-information-form__row">
                  <Textarea
                    name="arrivalCargoDescription"
                    rows={6}
                    placeholder="Brief description of the cargo at arrival"
                    label="Brief description of the cargo at arrival *"
                  />
                  <Textarea
                    name="departureCargoDescription"
                    rows={6}
                    placeholder="Brief description of the cargo at departure"
                    label="Brief description of the cargo at departure"
                  />
                </div>
                <div className="cargo-information-form__row">
                  <Input
                    name="arrivalCargoVolume"
                    type="number"
                    placeholder="Volume/qty of the cargo at arrival (m3)"
                    label="Volume/qty of the cargo at arrival (m3)"
                  />
                  <Input
                    name="departureCargoVolume"
                    type="number"
                    placeholder="Volume/qty of the cargo at departure (m3)"
                    label="Volume/qty of the cargo at departure (m3)"
                  />
                </div>
                <div className="cargo-information-form__row">
                  <RadioButtonsCustom
                    name="isTransportingHeavyOils"
                    label="Are you carrying commercially heavy fuel oil, tar, bitumen or heavy crude oil? *"
                    options={vesselCarryingCargoOptions}
                  />
                </div>
                <div className="dangerous-goods-form__row">
                  <DangerousGoodsOptionsCheckboxGroup
                    name="dangerousGoodsType"
                    label="Select the options that fit better: *"
                    options={dangerousGoodsTypeOptions}
                  />
                </div>
              </div>
            ) : null}
            <SkipAndNextButtons
              next={`/${params.uid}/cargo/documents`}
              back={`/${params.uid}/inspections`}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default CargoInformation;
